import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/router"
import { useRef } from "react"
import { twMerge } from "tailwind-merge"
import MarketingHeading from "@/components/common/MarketingHeading"
import { BlogImage } from "@/components/resources/components/BlogImage"
import { Hero } from "@/components/resources/components/Hero"
import { LargeTeaser, TeaserGrid } from "@/components/resources/components/TeaserGrid"
import { makeButtonClassName } from "@/components/resources/components/ui/Button"
import type { CategoryType, Teaser } from "@/components/resources/data/posts.types"
import { getOGImageRatioWidth } from "@/constants/blog-image"
import { BASE_BLOG_PATH } from "@/constants/urls"
import { NewsletterForm } from "../components/NewsletterForm"
import { formatDate } from "../components/ui/utils"

interface Props {
  title: string
  entries: Teaser[]
  lead: string
  category?: CategoryType
}

const PER_PAGE = 9

interface PaginationProps {
  page: number
  nextPage: number
  limit: number
  postsRemaining: boolean
  latestNewIndex: number
}

type UsePaginationProps<T> = [T[], PaginationProps]

const useRoutedPagination = <T,>(
  items: T[],
  { initialOffset = 0 }: { initialOffset?: number } = {}, // Offset allows considering eg. newsletter which is not included in items
): UsePaginationProps<T> => {
  const {
    query: { p },
  } = useRouter()
  const page = Number.parseInt((p && Array.isArray(p) ? p[0] : p) ?? "0", 10)
  const limit = (page ? Math.min(items.length, PER_PAGE * (page + 1)) : PER_PAGE) + initialOffset

  return [
    items.slice(0, limit),
    {
      page,
      nextPage: page + 1,
      limit,
      postsRemaining: items.length + initialOffset > limit,
      latestNewIndex: limit - PER_PAGE + 1 - initialOffset,
    },
  ]
}

const sortTeasers = (items: Teaser[], noSticky = false) => {
  const sortedTeasers: [Teaser[], Teaser[]] = [[], []]
  for (const item of items) {
    if (!item.sticky || noSticky) {
      sortedTeasers[1].push(item)
    } else {
      sortedTeasers[0].push(item)
    }
  }

  return sortedTeasers
}

const ShowMore = ({ nextPage, postsRemaining }: PaginationProps) => {
  // Relative to the route, not the url: on `/resources/[category]` a bare `?p=1` cannot be resolved
  const { pathname, query } = useRouter()
  return (
    <Link
      href={{ pathname, query: { ...query, p: nextPage } }}
      scroll={false}
      shallow
      className={twMerge(
        makeButtonClassName("medium", "default", "neutral"),
        "mt-4",
        !postsRemaining ? "opacity-50 cursor-not-allowed pointer-events-none" : "",
      )}
      rel="next"
    >
      Load more
    </Link>
  )
}

const renderStickies = (items: Teaser[]) => {
  return (
    <div className="flex flex-col gap-2 mb-4">
      {items.map((teaser) => (
        <LargeTeaser teaser={teaser} key={teaser.id ?? teaser.slug} />
      ))}
    </div>
  )
}

const Published = ({
  publishedAt,
  className,
}: Pick<Teaser, "publishedAt"> & { className?: string }) =>
  publishedAt ? (
    <p
      className={`text-xs text-mono tracking-[2px] font-medium mb-2 uppercase text-fg-secondary ${className || ""}`}
    >
      <time dateTime={formatDate(new Date(publishedAt))}>
        {formatDate(new Date(publishedAt), true)}
      </time>
    </p>
  ) : null

export const CategoryArchive = ({ entries, title, lead, category }: Props) => {
  const ref = useRef<null | HTMLDivElement>(null)
  const isPathHome = category === undefined
  // const isPathNewsletter = category === "newsletter"
  const isPathNewsletter = false
  const canShowHero = !isPathHome && !isPathNewsletter
  // const canShowNewsletter = isPathNewsletter || isPathHome
  const showFeaturedPost = !category

  let sticky: Teaser[] = []
  let teasers: Teaser[] = []
  let featuredPost: Teaser | undefined
  if (showFeaturedPost) {
    const featuredPosts = entries.slice(0, 1)
    const restOfPosts = entries.slice(1)
    ;[sticky, teasers] = sortTeasers(restOfPosts)
    featuredPost = featuredPosts[0]
  } else {
    ;[sticky, teasers] = sortTeasers(entries)
  }
  const [shownPosts, pagination] = useRoutedPagination(teasers, {
    initialOffset: 0,
  })

  return (
    <article className="flex flex-col mb-16">
      {canShowHero ? <Hero title={title} lead={lead} /> : null}
      {/* {canShowNewsletter ? <NewsletterForm /> : null} */}
      {showFeaturedPost && featuredPost ? (
        <section className="flex flex-col lg:flex-row gap-12 mb-14 mt-6">
          <Link
            href={`${BASE_BLOG_PATH}${featuredPost.category ? `/${featuredPost.category}` : ""}/${featuredPost.slug}`}
            className="w-full lg:w-2/3"
          >
            <div className="bg-bg-surface rounded-2xl overflow-hidden">
              <div className="bg-cover bg-center">
                {/* Add image or background image based on the teaser */}
                {typeof featuredPost.image === "string" ? (
                  <Image
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-auto aspect-blog-cover rounded-lg"
                    width={800}
                    height={getOGImageRatioWidth(800)}
                    quality={100}
                    sizes="(max-width: 1024px) 100vw, 66vw"
                  />
                ) : (
                  <BlogImage
                    width={800}
                    height={getOGImageRatioWidth(800)}
                    alt={featuredPost.title}
                    className="w-full h-auto aspect-blog-cover rounded-lg"
                    {...{
                      category: featuredPost.category,
                      image: featuredPost.image,
                      releaseNumber: featuredPost.releaseNumber,
                      projectMeta: featuredPost.projectMeta,
                    }}
                  />
                )}
              </div>
            </div>
            <div className="mt-6">
              <MarketingHeading level={3} className="text-4xl mb-2">
                {featuredPost.title}
              </MarketingHeading>
              <p className="text-fg-secondary mb-4 text-pretty">{featuredPost.teaserText}</p>
              <Published publishedAt={featuredPost.publishedAt} />
            </div>
          </Link>
          <div className="flex flex-col gap-2 flex-1">
            <NewsletterForm />
          </div>
        </section>
      ) : null}
      {entries.length ? (
        // div just for the ref
        <div ref={ref}>
          {sticky && renderStickies(sticky)}
          <TeaserGrid teasers={shownPosts} />
          {entries.length > PER_PAGE ? (
            <div className="pt-4 pb-8 flex justify-center">
              <ShowMore {...pagination} />
            </div>
          ) : null}
        </div>
      ) : (
        <div className="py-12 text-center">
          <p>No posts in this category.</p>
        </div>
      )}
    </article>
  )
}
